import message from "@/utils/globalMessage";
import React, { useState } from "react"
import { api } from '@/redux/api';
import { useRequest } from "ahooks";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, Row, Col, Input, Steps, Collapse, Button, Space, Card, Spin, Typography } from "antd";
import CustomDatePicker from "@components/CustomDatePicker";
import { Radio } from "antd";
import moment from "moment";
import styled from "styled-components";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { EventName, useEventBus } from "@/hooks/useEventBus";
import { useAuth, AUTH_LEVEL } from "@/hooks/use-auth";
import { buildFacilityPath, getFacilityContext, getLeafFromPathname } from "@/utils/route-context";
import { useSelector } from "react-redux";

const { Title } = Typography;
const Label = styled.span`
    font-weight: 600;
    color: #6A74A5 !important;
    font-size: 11px;
    margin-right: 10px;
    padding: 12px 0;
    display: inline-block;
`

const infos = []

export const ElecDetail = ({ isAdd = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();
    const facilityContext = useSelector((state) => getFacilityContext(state));
    const electricityListPath = buildFacilityPath('electricity', facilityContext) || '/switch-view';
    const isCreatePage = isAdd || getLeafFromPathname(location.pathname, "facility") === 'create-electricity';
    const [isEditing, setIsEditing] = useState(isCreatePage);
    const { closeTab, renameTab, refreshTab } = useKeepAliveContext();
    const canWrite = useAuth('electricity', AUTH_LEVEL.WRITE);

    const { data = {}, loading: fetchLoading, error, runAsync: refetch } = useRequest(
        () => api.getElectricityById(id),
        {
            ready: !isCreatePage,
            onSuccess: (data) => {
                form.setFieldsValue(data);
                renameTab(`/ElecDetail/${data.id}`, 'Eletricity Details');
            },
            onError: (err) => {
                console.error('Failed to fetch electricity details:', err);
                message.error('Failed to fetch electricity details');
            }
        }
    );

    useEventBus(EventName.ELECTRICITY_UPDATE, (data) => {
        if(data.id === +id) {
            refetch();
        }
    });

    useEventBus(EventName.ELECTRICITY_DELETE, (deleteId) => {
        if(deleteId === +id) {
            closeTab();
        }
    });

    const save = () => {
        form.validateFields().then(values => {
            if (isCreatePage) {
                api.createElectricity(values).then(res => {
                    if (res.status === 201 || res.status === 200) {
                        message.success('Created successfully');
                        refreshTab(electricityListPath);
                        closeTab();
                        navigate(electricityListPath);
                    }
                }).catch(errorInfo => {
                    console.log('createElectricity', errorInfo);
                    message.error('Failed to create electricity record');
                });
            } else {
                api.editElectricity(id, values).then(res => {
                    if (res.status === 200) {
                        message.success('Save successfully');
                        refetch();
                        setIsEditing(false);
                    }
                }).catch(errorInfo => {
                    console.log('saveUser', errorInfo)
                });
            }
        }).catch(errorInfo => {
            console.log('saveUser', errorInfo)
        });
    }

    const handleCancel = () => {
        setIsEditing(false);
        if (data) {
            form.setFieldsValue(data);
        }
    }

    return (
        <div style={{ padding: '20px' }}>
            <Spin spinning={isCreatePage ? false : fetchLoading} tip="Loading...">
                <Card
                    title={<Title level={4}>{isCreatePage ? 'Create Electricity' : 'Eletricity Details'}</Title>}
                    extra={
                        isEditing ? (
                            <>
                                <Space size={16}>
                                    {isCreatePage ? (
                                        <Button onClick={() => { closeTab(); navigate(electricityListPath); }} style={{ marginRight: 26 }}>
                                            Cancel
                                        </Button>
                                    ) : (
                                        <Button onClick={handleCancel} style={{ marginRight: 26 }}>
                                            Cancel
                                        </Button>
                                    )}
                                    <Button
                                        type="primary"
                                        onClick={save}
                                        style={{ marginRight: 8 }}
                                    >
                                        Save
                                    </Button>
                                </Space>
                            </>
                        ) : (
                            canWrite && <Space size={36}>
                                <Button type="primary" onClick={() => setIsEditing(true)}>
                                    Edit
                                </Button>
                            </Space>
                        )
                    }
                >
                    <Form
                        form={form}
                        layout="vertical"
                        disabled={!isEditing}
                        autoComplete="off">
                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Service Start Date"
                                    name="service_start_date"
                                    rules={[{ required: true, message: 'Please select service start date!' }]}
                                >
                                    <CustomDatePicker format='YYYY-MM-DD' disabled={!isEditing} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Service End Date"
                                    name="end_start_date"
                                    rules={[{ required: true, message: 'Please select service end date!' }]}
                                >
                                    <CustomDatePicker format='YYYY-MM-DD' disabled={!isEditing} />
                                </Form.Item>
                            </Col>

                            <Col xs={24}>
                                <Form.Item
                                    label="Description"
                                    name="description"
                                >
                                    <Input.TextArea rows={3} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Service Month Manual Override"
                                    name="manual_update"
                                >
                                    <Radio.Group defaultValue={false}>
                                        <Radio value={true}>True</Radio>
                                        <Radio value={false}>False</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Service Month"
                                    name="service_month"
                                >
                                    <CustomDatePicker format='YYYY-MM-DD' disabled={!isEditing} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Usage (kWh)"
                                    name="usage"
                                    rules={[{ required: true, message: 'Please input usage!' }]}
                                >
                                    <Input type="number" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Avg. OAT(°F)"
                                    name="avgoat"
                                    rules={[{ required: true, message: 'Please input average OAT!' }]}
                                >
                                    <Input type="number" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Costs($)"
                                    name="costs"
                                    rules={[{ required: true, message: 'Please input costs!' }]}
                                >
                                    <Input type="number" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Adjustment(KWH)"
                                    name="adjustment"
                                >
                                    <Input type="number" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Label>Created By:</Label>{data?.createdby_name}
                            </Col>

                            <Col xs={24} md={12}>
                                <Label>Created Date:</Label>{data.created_date ? moment(data.created_date).format('YYYY-MM-DD HH:mm') : ''}
                            </Col>

                            <Col xs={24} md={12}>
                                <Label>Modified By:</Label>{data?.lastModified_name}
                            </Col>

                            <Col xs={24} md={12}>
                                <Label>Modified Date:</Label>{data.modified_date ? moment(data.modified_date).format('YYYY-MM-DD HH:mm') : ''}
                            </Col>

                            <Form.Item name="id" hidden />
                        </Row>
                    </Form>
                </Card>
            </Spin>

        </div>
    );
};
